import { createSharedComposable } from '@vueuse/core';
import Keycloak from 'keycloak-js';
import { useEventHook } from '@deepcloud/deep-ui-utils/vue';

export type KeycloakEvents =
  | 'onActionUpdate'
  | 'onAuthError'
  | 'onAuthLogout'
  | 'onAuthRefreshSuccess'
  | 'onAuthRefreshError'
  | 'onReady'
  | 'onTokenExpired'
  | 'onAuthSuccess';

export const useKeycloak = createSharedComposable(() => {
  const initOptions = {
    enableLogging:
      import.meta.env.VITE_ENVIRONMENT === 'local' ||
      import.meta.env.VITE_ENVIRONMENT === 'dev',
    url: `${import.meta.env.VITE_IDP_URL}/${import.meta.env.VITE_IDP_URL_AUTH}`,
    realm: import.meta.env.VITE_IDP_REALM,
    clientId: import.meta.env.VITE_IDP_CLIENT_ID
  };

  const keycloak = new Keycloak(initOptions);

  const onActionUpdateEvent = useEventHook();
  keycloak.onActionUpdate = () => {
    onActionUpdateEvent.trigger();
  };
  const onAuthErrorEvent = useEventHook();
  keycloak.onAuthError = () => {
    onAuthErrorEvent.trigger();
  };
  const onAuthLogoutEvent = useEventHook();
  keycloak.onAuthLogout = () => {
    onAuthLogoutEvent.trigger();
  };
  const onAuthRefreshSuccessEvent = useEventHook();
  keycloak.onAuthRefreshSuccess = () => {
    onAuthRefreshSuccessEvent.trigger();
  };
  const onAuthRefreshErrorEvent = useEventHook();
  keycloak.onAuthRefreshError = () => {
    onAuthRefreshErrorEvent.trigger();
  };
  const onReadyEvent = useEventHook();
  keycloak.onReady = () => {
    onReadyEvent.trigger();
  };
  const onTokenExpiredEvent = useEventHook();
  keycloak.onTokenExpired = () => {
    onTokenExpiredEvent.trigger();
  };
  const onAuthSuccessEvent = useEventHook();
  keycloak.onAuthSuccess = () => {
    onAuthSuccessEvent.trigger();
  };

  function addListener(event: KeycloakEvents, fn: () => void) {
    switch (event) {
      case 'onActionUpdate':
        onActionUpdateEvent.on(fn);
        break;
      case 'onAuthError':
        onAuthErrorEvent.on(fn);
        break;
      case 'onAuthLogout':
        onAuthLogoutEvent.on(fn);
        break;
      case 'onAuthRefreshSuccess':
        onAuthRefreshSuccessEvent.on(fn);
        break;
      case 'onAuthRefreshError':
        onAuthRefreshErrorEvent.on(fn);
        break;
      case 'onReady':
        onReadyEvent.on(fn);
        break;
      case 'onTokenExpired':
        onTokenExpiredEvent.on(fn);
        break;
      case 'onAuthSuccess':
        onAuthSuccessEvent.on(fn);
        break;

      default:
        console.error(`Not supported keycloak event: ${event}`);
    }
  }

  function removeListener(event: KeycloakEvents, fn: () => void) {
    switch (event) {
      case 'onActionUpdate':
        onActionUpdateEvent.off(fn);
        break;
      case 'onAuthError':
        onAuthErrorEvent.off(fn);
        break;
      case 'onAuthLogout':
        onAuthLogoutEvent.off(fn);
        break;
      case 'onAuthRefreshSuccess':
        onAuthRefreshSuccessEvent.off(fn);
        break;
      case 'onAuthRefreshError':
        onAuthRefreshErrorEvent.off(fn);
        break;
      case 'onReady':
        onReadyEvent.off(fn);
        break;
      case 'onTokenExpired':
        onTokenExpiredEvent.off(fn);
        break;
      case 'onAuthSuccess':
        onAuthSuccessEvent.off(fn);
        break;

      default:
        console.error(`Not supported keycloak event: ${event}`);
    }
  }

  return {
    keycloak,
    addListener,
    removeListener
  };
});
